import React, { memo, useMemo } from 'react';
import { HelpCircle, BookOpen } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';

interface HelpButtonProps {
  guideId: string;
  variant?: 'button' | 'icon' | 'floating';
  size?: 'sm' | 'md' | 'lg';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  className?: string;
  children?: React.ReactNode;
}

export const HelpButton: React.FC<HelpButtonProps> = memo(({
  guideId,
  variant = 'button',
  size = 'md',
  position = 'top-right',
  className = '',
  children
}) => {
  const { openHelp, preferences, userRole, hasAccess } = useHelp();

  const handleClick = () => {
    if (hasAccess(guideId, userRole || '')) {
      openHelp(guideId);
    }
  };

  const isSeen = preferences.seenGuides.includes(guideId);

  const sizeClasses = useMemo(() => ({
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-2',
    lg: 'text-base px-4 py-2'
  }), []);

  const iconSizes = useMemo(() => ({
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }), []);

  const positionClasses = useMemo(() => ({
    'top-right': 'fixed top-4 right-4 z-40',
    'top-left': 'fixed top-4 left-4 z-40',
    'bottom-right': 'fixed bottom-4 right-4 z-40',
    'bottom-left': 'fixed bottom-4 left-4 z-40'
  }), []);

  // Only hide buttons if we have a valid user role and access check fails
  // If userRole is null/undefined, show the button (default behavior)
  if (userRole && !hasAccess(guideId, userRole)) {
    return null;
  }

  if (variant === 'floating') {
    return (
      <button
        onClick={handleClick}
        className={`${positionClasses[position]} bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-105 ${className}`}
        title="Help"
      >
        <HelpCircle className="h-6 w-6" />
        {!isSeen && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></span>
        )}
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center justify-center text-gray-600 hover:text-blue-600 dark:text-dark-text-secondary dark:hover:text-blue-400 transition-colors relative ${className}`}
        title="Help"
      >
        <HelpCircle className={iconSizes[size]} />
        {!isSeen && (
          <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full"></span>
        )}
      </button>
    );
  }

  // Default button variant
  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors relative ${sizeClasses[size]} ${className}`}
    >
      <BookOpen className={iconSizes[size]} />
      <span>{children || 'Help'}</span>
      {!isSeen && (
        <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-400 rounded-full"></span>
      )}
    </button>
  );
});